<template>
  <div>
    <!-- Field label with required indicator -->
    <label class="label" :class="{ isRequired: isRequired }">
      {{ label }}
    </label>

    <!-- Main dropdown container -->
    <b-dropdown
      aria-role="list"
      expanded
      :class="$style.dropdown"
      :can-close="true"
      trap-focus
      :close-on-click="false"
      :disabled="disabled"
      :position="position"
      v-bind="$props"
    >
      <!-- Trigger button showing selected values and dropdown icon -->
      <button
        :class="{
          'button simple-dropdown-button': true,
          'warning-field': showRequiredValidationError && !active,
          'ellipsis-text-button': true,
        }"
        slot="trigger"
        slot-scope="{ active }"
      >
        <span v-if="doesIncludeSeparator(buttonDisplayText)">
          {{ getValues(buttonDisplayText)[0] }}
          <b>{{ getTruncateValue(buttonDisplayText) }}</b>
        </span>
        <span v-else>
          {{ getTruncateValue(buttonDisplayText) }}
        </span>
        <b-icon :icon="active ? 'menu-up' : 'menu-down'" :class="$style.icon">
        </b-icon>
      </button>

      <!-- Optional search input -->
      <b-field v-if="showSearch" class="search-bar">
        <b-input
          type="text"
          v-model="searchQuery"
          placeholder="Search"
          icon="magnify"
          pack="mdi"
        />
      </b-field>

      <!-- Message when no options match -->
      <div v-if="!filteredOptions.length" class="padding-8">
        <center>No Data Found</center>
      </div>

      <!-- Options list -->
      <div v-else>
        <!-- Select All checkbox -->
        <b-dropdown-item
          aria-role="listitem"
          key="dropdown_select_all"
          :class="{
            'simple-dropdown-item': true,
            [$style.noPadding]: true,
          }"
        >
          <b-checkbox
            :value="selectAllChecked"
            @input="toggleSelectAll()"
            :disabled="isSelectAllDisabled"
          >
            Select All
          </b-checkbox>
        </b-dropdown-item>

        <!-- Individual options -->
        <b-dropdown-item
          aria-role="listitem"
          v-for="option in filteredOptions"
          :key="option.id"
          :class="{
            [$style.noPadding]: true,
            [$style.disabled]: isOptionDisabled(option),
          }"
          @click.native="toggleOption(option)"
          :disabled="isOptionDisabled(option)"
        >
          <b-checkbox
            v-model="selectedOptions"
            :native-value="option.id"
            :disabled="isOptionDisabled(option)"
            :class="{ [$style.disabledLabel]: isOptionDisabled(option) }"
          >
            <b-tooltip
              :active="option.value.length > textLength"
              :label="getOptionTooltipValue(option.value)"
              position="is-bottom"
              multilined
            >
              <span v-if="doesIncludeSeparator(option.value)">
                {{ getValues(option.value)[0] }}
                <b>{{ getTruncateValue(option.value) }}</b>
              </span>
              <span v-else>
                {{ getTruncateValue(option.value) }}
              </span>
            </b-tooltip>
          </b-checkbox>
        </b-dropdown-item>
      </div>

      <!-- Custom slot for footer content -->
      <slot name="footer" />
    </b-dropdown>

    <!-- Error shown when required field is left empty -->
    <div v-if="showRequiredValidationError" class="warning-message">
      <small>This field is required</small>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

enum DropdownPosition {
  TOP_RIGHT = 'is-top-right',
  TOP_LEFT = 'is-top-left',
  BOTTOM_RIGHT = 'is-bottom-right',
  BOTTOM_LEFT = 'is-bottom-left',
}

@Component({})
export default class DropdownMultipleSelect extends Vue {
  @Prop({ type: Array, default: () => [] })
  readonly value!: (string | number)[];
  @Prop({ type: Array, required: true })
  readonly options!: Array<{
    id: string | number; // string or number
    value: string;
  }>;
  @Prop({ type: String, required: true })
  readonly label!: string;
  @Prop({ type: String, default: 'Select...' })
  readonly placeholder!: string;
  @Prop({ type: Boolean, default: true })
  readonly multi!: boolean;
  @Prop({ type: Boolean, default: false })
  readonly showSearch!: boolean;
  @Prop({ type: Boolean, default: false })
  readonly disabled!: boolean;
  @Prop({ type: String, default: '' })
  readonly disableDataInfoMsg!: string;
  @Prop({ type: Array, default: () => [] })
  readonly disabledOptions!: (string | number)[]; // Expected format is always array of string or number
  @Prop({ type: String, default: DropdownPosition.BOTTOM_RIGHT })
  readonly position!: DropdownPosition;
  @Prop({ type: Boolean, default: false })
  readonly isRequired!: boolean;
  @Prop({ type: Boolean, required: false })
  public isCreateProject!: Boolean;

  public textLength: number = this.isCreateProject ? 120 : 60; // here values added based on UI components
  public searchQuery: string = '';
  public selectAllChecked: boolean = false;

  public doesIncludeSeparator(value: string): boolean {
    return value.includes('||');
  }

  public getValues(value: string): string[] {
    return value.split('||');
  }

  public getTruncateValue(optionValue: string): string {
    const value: string = this.doesIncludeSeparator(optionValue)
      ? this.getValues(optionValue)[1]
      : this.getValues(optionValue)[0];

    if (value.length > this.textLength) {
      return `${value.substring(0, this.textLength - 5)} ...`;
    }
    return value;
  }

  public getOptionTooltipValue(value: string) {
    const values: string[] = value.split('||');
    return values.join(' ');
  }

  get selectedOptions(): (string | number)[] {
    return this.value.map((valueId) => valueId);
  }

  set selectedOptions(ids: (string | number)[]) {
    this.$emit(
      'input',
      this.options.filter((o) => ids.includes(o.id)).map((o) => o.id),
    );
  }

  /** Filter options based on search query */
  get filteredOptions() {
    if (!this.searchQuery) return this.options;

    return this.options.filter((option) =>
      option.value.toLowerCase().includes(this.searchQuery.toLowerCase()),
    );
  }

  /** Checks filter options that are NOT disabled, If there are no selectable options returns true */
  get isSelectAllDisabled() {
    const validSelectableOptions = this.filteredOptions.filter(
      (option) => !this.disabledOptions.includes(option.id),
    );
    return validSelectableOptions.length === 0;
  }

  get buttonDisplayText(): string {
    const selectedItems = this.selectedOptions.filter((op) =>
      this.selectedOptions.includes(op),
    );

    if (selectedItems.length === 0) return this.placeholder;
    if (selectedItems.length === this.options.length)
      return `All ${this.label}`;
    if (selectedItems.length === 1) {
      const getSelectedItem = this.options.find(
        (op) => op.id === selectedItems[0],
      );
      return getSelectedItem ? getSelectedItem.value : this.placeholder;
    }

    return `${selectedItems.length} ${this.label} selected`;
  }

  /** Show validation error if field is required but nothing selected */
  get showRequiredValidationError() {
    const selectedItems = this.options.filter((op) =>
      this.selectedOptions.includes(op.id),
    );

    return this.isRequired && this.options.length && !selectedItems.length;
  }

  /** Determine if an option should be disabled */
  private isOptionDisabled(option: {
    id: string | number; // string or number
    value: string;
    disabled?: boolean;
  }): boolean {
    return option.disabled === true || this.disabledOptions.includes(option.id);
  }

  /** Handle toggle an individual option's selection */
  private toggleOption(option: {
    id: string | number; // string or number
    value: string;
  }) {
    // For single-select mode, overwrite selection
    if (!this.multi) {
      if (this.selectedOptions.includes(option.id)) {
        this.selectedOptions = [];
      } else {
        this.selectedOptions = [option.id];
      }
      return;
    }

    // For multi-select, add or remove the option ID
    if (this.selectedOptions.includes(option.id)) {
      this.selectedOptions = this.selectedOptions
        .filter((selectedOption) => selectedOption !== option.id)
        .map((selectedOption) => selectedOption);
    } else {
      this.selectedOptions = [...this.selectedOptions, option.id];
    }

    this.syncSelectAll();
    this.$emit('input', this.selectedOptions);
  }

  /** Handle toggle 'Select All' functionality */
  private toggleSelectAll() {
    if (this.selectAllChecked) {
      /** remove all filtered options from selection */
      this.selectedOptions = this.options
        .filter((op) => {
          const a = this.filteredOptions.find((fop) => fop.id === op.id);
          if (!a) {
            return op.id;
          }
        })
        .map((op) => op.id);
    } else {
      /** add all enabled filtered options */
      const selectedItems = [...this.selectedOptions];
      this.filteredOptions.forEach((option) => {
        {
          if (
            !this.isOptionDisabled(option) &&
            !selectedItems.includes(option.id)
          ) {
            selectedItems.push(option.id);
          }
        }
      });

      this.selectedOptions = selectedItems;
    }

    /** update selectAll state and sync it */
    this.selectAllChecked = !this.selectAllChecked;
    this.syncSelectAll();
  }

  /** Sync 'Select All' checkbox state based on current selection */
  private syncSelectAll() {
    const selectedFilteredOptions = this.filteredOptions.filter((fp) =>
      this.selectedOptions.includes(fp.id),
    );

    this.selectAllChecked =
      this.filteredOptions.length > 0 &&
      selectedFilteredOptions.length === this.filteredOptions.length;
  }

  /** Reactively update Select All checkbox if value prop changes */
  @Watch('value', { immediate: true, deep: true })
  onValueChange() {
    this.syncSelectAll();
  }

  @Watch('searchQuery', { immediate: true, deep: true })
  onSearch() {
    this.syncSelectAll();
  }
}
</script>

<style lang="scss" module>
.dropdown :global {
  .dropdown-menu,
  .dropdown-content {
    padding: 0;
  }

  .dropdown-menu {
    top: 110%;
  }

  .dropdown-item {
    height: 43px;
    border-bottom: 1px solid $grey-border;
    display: flex;
    align-content: center;

    &:focus {
      outline-width: 0;
    }

    &:hover {
      background-color: #eff7ff !important;
      color: #444f51;
    }

    &.is-disabled {
      &,
      * {
        cursor: not-allowed;
      }
    }
  }

  .checkbox {
    width: 100%;
    padding: 11px 13px;

    .control-label {
      color: $dark;
      font-size: 16px;
      text-align: left;
      padding-left: 12px;
      font-weight: 500;
    }
  }
}

.noPadding {
  padding: 0;
}

.disabled {
  cursor: not-allowed;
  opacity: 0.6;

  &:hover {
    background-color: white;
  }
}
</style>

<style scoped>
.label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  text-align: left;
  color: #9f9f9f;
}

.label.isRequired:after {
  content: '*';
  color: #ff465d;
}

.search-bar {
  padding: 12px;
  margin-bottom: 0 !important;

  div {
    span {
      height: 2.75em !important;
      padding-left: 12px !important;
      color: #dfdfdf !important;
    }
    input {
      padding-left: 2.8em !important;
    }
  }
}

.warning-message {
  color: #ff465d;

  i {
    margin-right: 0.2rem;
  }
}

.warning-field {
  border: 1px solid #ff465d;
}

.ellipsis-text-button {
  span {
    text-overflow: ellipsis;
    overflow: hidden;
  }
}
</style>
